import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateLeadServedFilterQueries } from '../LeadServed/useLeadServedFilterInfiniteQuery';
import { invalidatePhoneCallCategoriesQueries } from './usePhoneCallCategoriesQuery';

const toastError = (fallback: string) => (error: Api.IOperationResultNoValue) =>
	toast.error(error?.systemMessage ?? fallback);

export function usePhoneCallCategoryAssignmentMutation() {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({
			assigned,
			categoryName,
			phoneCallId,
		}: {
			assigned: boolean;
			categoryName: string;
			phoneCallId: string;
		}) =>
			userSession.webServiceHelper.callAsync<Api.IPhoneCall>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: { category: categoryName },
					urlPath: `phonecall/${phoneCallId}/category`,
				}),
				assigned ? 'POST' : 'DELETE'
			),
		onError: toastError('Unable to update the call category'),
		onSuccess: () => invalidateLeadServedFilterQueries(),
	});
}

export function useCreatePhoneCallCategoryMutation({
	onSuccess,
	onError = toastError("Oh no! I couldn't create this Category."),
}: {
	onSuccess?: (category: Api.IPhoneCallCategory) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
} = {}) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: (category: Api.IPhoneCallCategory) =>
			userSession.webServiceHelper.callAsync<Api.IPhoneCallCategory>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: 'phonecall/category' }),
				'POST',
				category
			),
		onError,
		onSuccess: category => {
			invalidatePhoneCallCategoriesQueries();
			onSuccess?.(category);
		},
	});
}

export function useUpdatePhoneCallCategoryMutation() {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: (category: Api.IPhoneCallCategory) =>
			userSession.webServiceHelper.callAsync<Api.IPhoneCallCategory>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `phonecall/category/${category.id}` }),
				'PUT',
				category
			),
		onError: toastError('Unable to update the category'),
		onSuccess: () => invalidatePhoneCallCategoriesQueries(),
	});
}
